///
/// EarthBrowser
///
/// Created by Matt Giger on 8/21/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type cube } from "./cube"
import { type plane } from "./plane"
import { type sphere } from "./sphere"
import { type vec3 } from "./vector"

export type cull_result = "outside" | "intersect" | "inside"

/**
 * Represents a frustum in 3D space, typically used for view frustum culling.
 */
export class frustum {
	/** Array of planes defining the frustum boundaries */
	public plane: plane[] = []

	/**
	 * Creates a new frustum from an array of planes.
	 * @param planes - The planes defining the frustum.
	 */
	constructor(planes: plane[]) {
		this.plane = planes
	}

	/**
	 * Checks if a point is inside the frustum.
	 * @param pt - The point to check.
	 * @returns True if the point is inside the frustum, false otherwise.
	 */
	public contains(pt: vec3): boolean {
		for (let p = 0; p < 6; ++p) {
			const dist = this.plane[p].distance(pt)
			if (dist < 0) {
				return false
			}
		}
		return true
	}

	/**
	 * Checks if a sphere intersects with the frustum.
	 * @param s - The sphere to check.
	 * @returns The cull result (outside, intersect, or inside).
	 */
	public intersects(s: sphere): cull_result {
		// A sphere is outside as soon as any single plane rejects it, but
		// "intersect" can only be decided after checking every plane: straddling
		// one plane's infinite extension means nothing if another plane rejects
		let result: cull_result = "inside"
		for (let p = 0; p < 6; ++p) {
			const dist = this.plane[p].distance(s.center)
			if (dist < -s.radius) {
				return "outside"
			}

			if (dist < s.radius) {
				result = "intersect"
			}
		}
		return result
	}

	/**
	 * Checks if a cube intersects with the frustum.
	 * @param c - The cube to check.
	 * @returns The cull result (outside, intersect, or inside).
	 */
	public intersects_cube(c: cube): cull_result {
		let total_in = 0

		for (let p = 0; p < 6; ++p) {
			let pt_in = 1,
				num_in = 8
			for (let i = 0; i < 8; ++i) {
				if (this.plane[p].distance(c.corner(i)) < 0) {
					pt_in = 0
					--num_in
				}
			}

			if (num_in === 0) {
				return "outside"
			}

			total_in += pt_in // 1 if this point inside all planes
		}

		if (total_in === 6) {
			return "inside"
		}

		return "intersect"
	}
}
